<template>
  <VaInput
    v-native-input-describedby="inputAriaDescribedby"
    v-native-input-required="required"
    :model-value="modelValue"
    :type="type"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :error="error"
    :id="inputId"
    :name="inputName"
    v-bind="optionalBindings"
    @update:model-value="onUpdate"
    @blur="onBlur"
    @focus="onFocus"
  />
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'
import type { DirectiveBinding, ObjectDirective } from 'vue'
import { VaInput } from 'vuestic-ui'

export interface AppInputProps {
  readonly modelValue: string
  readonly type?: 'text' | 'date' | 'time' | 'number' | 'email' | 'password'
  readonly placeholder?: string
  readonly disabled?: boolean
  readonly readonly?: boolean
  readonly id?: string
  readonly name?: string
  readonly inputAriaLabel?: string
  readonly inputAriaDescribedby?: string | undefined
  readonly inputClass?: string
  readonly min?: string | number
  readonly max?: string | number
  readonly error?: boolean
  readonly required?: boolean
  readonly autofocus?: boolean
}

/**
 * Native constraints and Vuestic's input-only accessible name are
 * deliberately omitted from `withDefaults` so their defaults stay genuinely
 * `undefined` (Vuestic's prop types reject an explicit `undefined` under
 * `exactOptionalPropertyTypes: true`). They're forwarded only when the
 * consumer sets a concrete value.
 */
const props = withDefaults(defineProps<AppInputProps>(), {
  type: 'text',
  placeholder: '',
  disabled: false,
  readonly: false,
  error: false,
  required: false,
  autofocus: false,
})

/**
 * Auto-generates `id` and `name` via `useId()` so consumers don't need to pass them; pass them explicitly when you need a stable cross-component handle (e.g. for `aria-describedby` from another component).
 */
const generatedId = useId()
const inputId = computed(() => props.id ?? generatedId)
const inputName = computed(() => props.name ?? generatedId)

/**
 * Vuestic 1.10 forwards `inputAriaLabel` / `inputAriaLabelledby` to its native
 * input, but consumes `inputAriaDescribedby` in `VaInputWrapper` without adding
 * it to that native node. Keep Vuestic's field, validation, and focus state
 * intact while mirroring only this missing native attribute at our wrapper
 * boundary. The update hook covers conditional validation/help IDs.
 */
function syncNativeInputDescribedby(
  element: HTMLElement,
  binding: DirectiveBinding<string | undefined>,
): void {
  const input = element.matches('input')
    ? element as HTMLInputElement
    : element.querySelector('input')
  if (input === null) return

  const describedby = binding.value?.trim()
  if (describedby === undefined || describedby === '') {
    input.removeAttribute('aria-describedby')
    return
  }
  input.setAttribute('aria-describedby', describedby)
}

const vNativeInputDescribedby: ObjectDirective<HTMLElement, string | undefined> = {
  mounted: syncNativeInputDescribedby,
  updated: syncNativeInputDescribedby,
}

function syncNativeInputRequired(
  element: HTMLElement,
  binding: DirectiveBinding<boolean>,
): void {
  const input = element.matches('input')
    ? element as HTMLInputElement
    : element.querySelector('input')
  if (input === null) return

  if (binding.value) {
    input.setAttribute('required', '')
    input.setAttribute('aria-required', 'true')
    return
  }
  input.removeAttribute('required')
  input.setAttribute('aria-required', 'false')
}

const vNativeInputRequired: ObjectDirective<HTMLElement, boolean> = {
  mounted: syncNativeInputRequired,
  updated: syncNativeInputRequired,
}

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur'): void
  (e: 'focus'): void
}>()

const optionalBindings = computed<{
  name?: string
  inputAriaLabel?: string
  inputAriaDescribedby?: string
  inputClass?: string
  min?: string | number
  max?: string | number
}>(() => {
  const out: {
    name?: string
    inputAriaLabel?: string
    inputAriaDescribedby?: string
    inputClass?: string
    min?: string | number
    max?: string | number
  } = {}
  if (props.name !== undefined) out.name = props.name
  if (props.inputAriaLabel !== undefined) out.inputAriaLabel = props.inputAriaLabel
  if (props.inputAriaDescribedby !== undefined) {
    out.inputAriaDescribedby = props.inputAriaDescribedby
  }
  if (props.inputClass !== undefined) out.inputClass = props.inputClass
  if (props.min !== undefined) out.min = props.min
  if (props.max !== undefined) out.max = props.max
  return out
})

/**
 * Vuestic's `VaInput` declares `modelValue: string | number | null` and emits
 * a `Number` for `type="number"` inputs (the native `<input type="number">`
 * coerces its `value` to a number before forwarding). Our public prop type
 * promises a `string` — coerce every update here so callers that bind
 * `v-model` to a `ref<string>` stay type-correct at runtime and Vue's prop
 * validator doesn't warn about a `Number` reaching a `string`-typed prop.
 */
function onUpdate(value: string | number | null): void {
  emit('update:modelValue', value === null || value === undefined ? '' : String(value))
}

function onBlur(): void {
  emit('blur')
}

function onFocus(): void {
  emit('focus')
}
</script>
